/**
 * 표 페이지 분할 순수 로직.
 * 렌더/DOM 의존 없음 — 측정값(행 top offset, 높이)을 입력으로 받아 계산만 수행.
 * 편집 캔버스 A4 낱장 분할(A안)의 기초 모듈.
 */

/** 병합셀 정보(행 인덱스 + rowspan). rowspan<=1은 경계에 영향 없음. */
export interface RowSpanCell {
  row: number;
  rowspan: number;
}

/**
 * 각 행 경계(before-row r)에서 페이지를 끊어도 되는지 배열로 반환. 길이 = totalRows+1.
 * canCutBefore[0]=true, canCutBefore[totalRows]=true(표 끝은 항상 안전).
 * 경계 b(1..totalRows-1)는 어떤 셀이 row<b && row+rowspan>b 로 관통하면 false(그 경계에서 자르면
 * 병합셀이 두 페이지에 걸침).
 */
export function computeSafeCutRows(cells: RowSpanCell[], totalRows: number): boolean[] {
  // 기본은 모든 경계 안전(true). 병합셀이 관통하는 경계만 false로 내린다.
  const safe = new Array<boolean>(totalRows + 1).fill(true);

  for (const cell of cells) {
    if (cell.rowspan <= 1) continue; // 단일 행 셀은 경계에 영향 없음
    const start = cell.row;
    const end = cell.row + cell.rowspan; // 반열림 끝(관통 범위: start < b < end)
    for (let b = start + 1; b < end; b += 1) {
      // 경계 b가 셀 내부를 관통 → 그 경계에서 자르면 병합셀이 쪼개짐
      if (b >= 1 && b <= totalRows - 1) {
        safe[b] = false;
      }
    }
  }

  // 표의 시작/끝 경계는 항상 안전
  safe[0] = true;
  safe[totalRows] = true;
  return safe;
}

/**
 * 표(페이지 상단에서 시작한다고 가정)를 페이지별 행 범위로 분할.
 * @param rowTops     각 행의 top offset(표 상단=0 기준, 오름차순, 길이 N=totalRows)
 * @param tableHeight 표 전체 높이(= 마지막 행 bottom)
 * @param pageHeight  페이지당 콘텐츠 높이(px, >0)
 * @param safeCutRows computeSafeCutRows 결과(길이 N+1)
 * @returns 페이지별 [startRow, endRow) 배열(반열림). 전체 [0,N)을 빠짐없이 덮고 겹치지 않음.
 */
export function planRowPages(
  rowTops: number[],
  tableHeight: number,
  pageHeight: number,
  safeCutRows: boolean[],
): Array<[number, number]> {
  const n = rowTops.length;
  if (n === 0) return [];

  // 방어: pageHeight<=0이면 1로 clamp
  const ph = pageHeight > 0 ? pageHeight : 1;

  // 행 r의 bottom offset(마지막 행은 tableHeight)
  const rowBottom = (r: number): number => (r + 1 < n ? rowTops[r + 1] : tableHeight);

  const EPS = 0.5; // 부동소수 비교 여유
  const pages: Array<[number, number]> = [];

  let start = 0;
  while (start < n) {
    const pageTop = rowTops[start];
    const maxBottom = pageTop + ph;

    // forward-scan: bottom<=maxBottom 이면서 safeCutRows[end]=true 인 가장 큰 end
    let end = start;
    for (let r = start; r < n; r += 1) {
      if (rowBottom(r) <= maxBottom + EPS) {
        // r행까지 페이지에 담을 수 있음 → 경계 r+1이 안전하면 후보로 채택
        const boundary = r + 1;
        if (boundary === n || safeCutRows[boundary]) {
          end = boundary;
        }
      } else {
        break; // 오름차순이라 더 이상 담을 수 없음
      }
    }

    // 한 행도 못 담거나 안전 end를 못 찾아 제자리면 강제로 최소 한 행 전진
    if (end <= start) {
      end = start + 1;
      // 병합셀이 경계를 관통하면 안전해질 때까지 계속 포함(무한루프 방지)
      while (end < n && !safeCutRows[end]) {
        end += 1;
      }
    }

    pages.push([start, end]);
    start = end;
  }

  return pages;
}
